import { isXeroAsIsTypeReview } from "@components/accountingMigration/isXeroAsIsTypeReview";
import { Task } from "@common/types/task";
import {
    useAccountingMigration,
    useImportedAccounts,
} from "@api/accountingIntegration.api";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { useOverlays } from "contexts/OverlayContext";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useScope } from "state/scopeContext.lib";
import { getNextTaskId } from "@components/tasks/useAdvanceToNextTask";

interface Props {
    selectedTask: Task;
}

const MapAccountsButton = ({
    workspaceId,
    entityId,
    nextTaskId,
}: {
    workspaceId: string;
    entityId: number;
    nextTaskId?: string;
}) => {
    const { openOverlay } = useOverlays();
    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const { data: migration } = useAccountingMigration(workspaceId, entityId);
    const { data: importedAccountsData } = useImportedAccounts(
        workspaceId,
        entityId,
        { orderBy: "name" },
    );
    const isTypeReview = isXeroAsIsTypeReview({
        externalSystem: migration?.externalSystem,
        isChartImportedAsIs: migration?.isChartImportedAsIs,
    });

    if (!migration) {
        return null;
    }

    const isImportedAccountsLoading = !importedAccountsData;

    const handleClick = () => {
        if (refuseIfViewOnly()) {
            return;
        }
        if (!isTypeReview) {
            openOverlay("coaMapping", {
                connectionWorkspaceId: workspaceId,
                entityId,
                nextTaskId,
            });
            return;
        }
        openOverlay("quickbooksMapAccounts", {
            connectionWorkspaceId: workspaceId,
            entityId,
            nextTaskId,
        });
    };

    return (
        <Button
            onClick={handleClick}
            disabled={isImportedAccountsLoading}
            variant="primary"
            size="md"
        >
            {isTypeReview ? "Review types" : "Map accounts"}
        </Button>
    );
};

const TaskMapChartOfAccountsAction = ({ selectedTask }: Props) => {
    const { workspaceId } = useScope();
    const entityId = selectedTask.entityIds?.[0];

    if (!entityId || !workspaceId) {
        return null;
    }

    return (
        <MapAccountsButton
            workspaceId={workspaceId}
            entityId={entityId}
            nextTaskId={getNextTaskId(selectedTask)}
        />
    );
};

export const MapChartOfAccountsCommandIsland = ({ selectedTask }: Props) => (
    <CommandIsland alignEnd className="mx-3">
        <TaskMapChartOfAccountsAction selectedTask={selectedTask} />
    </CommandIsland>
);
